import { isEmpty } from 'lodash';
import { node } from 'prop-types';
import { createContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { FEATURE_FLAG_KEYS, useFeatureFlag, useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { setFooterBanner } from 'store';

export const DashboardContext = createContext();

const DashboardContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { call: accountCall, loading: accountLoading, data: accountData } = useFetch({ filterUserCompany: true });
  const isTouloisirs = appService.isTouloisirs();
  const { enabled: sponsorshipEnabled } = useFeatureFlag(FEATURE_FLAG_KEYS.sponsorshipEnabled);

  const { currentUser } = useUser();
  const { call: userCall, loading: userLoading, data: user } = useFetch();
  const {
    call: consumptionsCall,
    loading: consumptionsLoading,
    data: consumptionsData,
    setData: setConsumptionsData
  } = useFetch({ withSetState: false });

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!currentUser?.serialNumber) return;

    userCall({
      service: 'castelis',
      path: `users/${currentUser.serialNumber}`,
      method: 'get'
    });
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getAccountPage = async (tabIdentifier) => {
    const tabName = tabs.find((tab) => tab.identifier === tabIdentifier)?.label;
    if (!tabName) return;

    const { data } =
      (await accountCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: 'api/account-pages',
        query: {
          filters: {
            tab: { $eq: tabName }
          }
        }
      })) || {};

    const bannerData = data?.body?.data?.[0]?.helpBanner;

    if (!isEmpty(bannerData)) {
      dispatch(setFooterBanner(bannerData));
    }
  };

  const navigateTo = (tab) => {
    navigate(routes.dashboard.children[tab.identifier].path);
  };

  /**
   * Tabs displayed in the dashboard
   * caption : text displayed in the tab in mobile view only
   * path : nested path of the tab (ex: for '/dashboard/mes-avantages' it will be 'mes-avantages')
   *        The path is used to determine the active tab
   * identifier : id used to identify the route. The identifier must match the key in routes.js file
   */
  const tabs = useMemo(() => {
    const dashboardTabs = [
      {
        caption: t('dashboard:tabs.caption.account'),
        identifier: 'account',
        icon: { name: 'user', style: 'fad' },
        label: t('dashboard:tabs.label.account'),
        onClick: navigateTo,
        path: 'mon-compte'
      },
      {
        caption: t('dashboard:tabs.caption.advantages'),
        identifier: 'benefits',
        icon: { name: 'star-exclamation', style: 'fad' },
        label: t('dashboard:tabs.label.advantages'),
        onClick: navigateTo,
        path: 'mes-avantages'
      },
      {
        caption: t('dashboard:tabs.caption.supportingDocuments'),
        identifier: 'documents',
        icon: { name: 'campfire', style: 'fad' },
        label: t('dashboard:tabs.label.supportingDocuments'),
        onClick: navigateTo,
        path: 'mes-demandes'
      },
      {
        caption: t('dashboard:tabs.caption.order'),
        identifier: 'orders',
        icon: { name: 'cart-shopping', style: 'fad' },
        label: t('dashboard:tabs.label.order'),
        onClick: navigateTo,
        path: 'mes-commandes'
      },
      {
        caption: t('dashboard:tabs.caption.data'),
        identifier: 'informations',
        icon: { name: 'id-badge', style: 'fad' },
        label: t('dashboard:tabs.label.data'),
        onClick: navigateTo,
        path: 'mes-informations'
      }
    ];

    if (sponsorshipEnabled) {
      dashboardTabs.push({
        caption: t('dashboard:tabs.caption.sponsorship'),
        identifier: 'sponsorship',
        icon: { name: 'handshake', style: 'fad' },
        label: t('dashboard:tabs.label.sponsorship'),
        onClick: navigateTo,
        path: 'mes-parrainages'
      });
    }

    if (appService.isArrivaj()) {
      // Remove document tab for Arrivaj
      dashboardTabs.splice(2, 1);
    }

    return dashboardTabs;
  }, [sponsorshipEnabled]);

  const getConsumption = async () => {
    const { data } =
      (await consumptionsCall({
        service: isTouloisirs ? 'castelis' : 'subvention',
        path: isTouloisirs
          ? `users/consos/${currentUser.serialNumber}`
          : `api/${currentUser.businessId}/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    if (!isEmpty(data?.body)) {
      if (isTouloisirs) {
        setConsumptionsData(data?.body?.consos?.[0]?.consos || []);
      } else {
        const formattedData = [
          {
            remaining: data.body.usedSubsidy / 100 || 0,
            max: data.body.grantedSubsidy / 100 || 0,
            label: data.body.financialGrantLabel || 'N/A'
          }
        ];
        setConsumptionsData(formattedData);
      }
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        tabs,
        navigateTo,
        user,
        userLoading,
        getAccountPage,
        accountData,
        accountLoading,
        consumptionsData,
        consumptionsLoading,
        getConsumption
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

DashboardContextProvider.propTypes = {
  children: node.isRequired
};

export default DashboardContextProvider;
